import { useState, useEffect, useCallback } from 'react';
import { usePanelOverlay } from '@/stores/uiStore';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { useSettingsStore, type ModelProvider } from '@/stores/settingsStore';
import { api } from '@/services/api';
import clsx from 'clsx';

const _PANEL_CLIP = 'polygon(10px 0, calc(50% - 60px) 0, calc(50% - 48px) 5px, calc(50% + 48px) 5px, calc(50% + 60px) 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)';

interface ModelOption {
  id: string;
  label: string;
  tier: string;
}

interface ProviderDef {
  id: ModelProvider;
  label: string;
  description: string;
  tag: string;
  color: string;
  models: ModelOption[];
}

const providers: ProviderDef[] = [
  {
    id: 'cerebras', label: 'Cerebras', description: 'Fast & cheap inference', tag: 'UPLINK', color: '#a855f7',
    models: [
      { id: 'llama3.1-8b', label: 'Llama 3.1 8B', tier: 'fast' },
      { id: 'qwen-3-235b-a22b-instruct-2507', label: 'Qwen 235B', tier: 'balanced' },
    ],
  },
  {
    id: 'claude', label: 'Claude', description: 'Nuanced reasoning', tag: 'UPLINK', color: '#ff8c00',
    models: [
      // Carter 2026-04-26: Haiku is the canonical default. The auto-select
      // rule (m.tier === 'balanced' && !override) picks the first
      // `balanced` model in the provider's list, so the default model
      // MUST be `balanced`. Sonnet/Opus are explicit upgrades.
      { id: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5', tier: 'balanced' },
      { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', tier: 'premium' },
      { id: 'claude-opus-4-6', label: 'Opus 4.6', tier: 'premium' },
    ],
  },
  {
    id: 'gemini', label: 'Gemini', description: 'Multimodal Flash', tag: 'UPLINK', color: '#4285F4',
    models: [
      { id: 'gemini-3.1-flash-lite-preview', label: 'Flash Lite', tier: 'fast' },
      { id: 'gemini-2.5-flash', label: 'Flash 2.5', tier: 'balanced' },
      { id: 'gemini-2.5-pro', label: 'Pro 2.5', tier: 'premium' },
    ],
  },
  {
    id: 'stark_protocol', label: 'Stark Protocol', description: 'Self-hosted Gemma 4', tag: 'LOCAL', color: '#00d4ff',
    models: [
      { id: 'default', label: 'Gemma 4 E4B', tier: 'fast' },
    ],
  },
];

export default function ModelPickerFloat() {
  const { isOpen: isOpenOverlay, toggle: toggleOverlay, close: closeOverlay } = usePanelOverlay('model_picker', 'panel');
  const isOpen = isOpenOverlay;
  const setIsOpen = (open: boolean) => { if (open) toggleOverlay(); else closeOverlay(); };
  const { modelPreference, setModelPreference, modelOverride, setModelOverride } = useSettingsStore();
  const [available, setAvailable] = useState<Set<string>>(new Set(providers.map((p) => p.id)));
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);

  useEffect(() => {
    const handler = () => toggleOverlay();
    window.addEventListener('jarvis-model-toggle', handler);
    return () => window.removeEventListener('jarvis-model-toggle', handler);
  }, []);

  useEffect(() => {
    api
      .get<{ id: string; available: boolean }[]>('/providers')
      .then((data) => setAvailable(new Set(data.filter((p) => p.available).map((p) => p.id))))
      .catch(() => {});
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 panel-backdrop"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="relative w-full max-w-[400px] mx-4 pointer-events-auto">
              <div className="absolute -inset-px pointer-events-none hud-beam-border" style={{ clipPath: _PANEL_CLIP }} />
              <div className="absolute -inset-px pointer-events-none" style={{ background: 'rgba(0, 212, 255, 0.08)', clipPath: _PANEL_CLIP }} />
              <div className="relative" style={{
                background: 'rgba(6, 8, 20, 0.92)',
                backdropFilter: 'blur(32px) saturate(1.4)',
                WebkitBackdropFilter: 'blur(32px) saturate(1.4)',
                clipPath: _PANEL_CLIP,
                boxShadow: '0 0 40px rgba(0, 0, 0, 0.5)',
              }}>
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3.5">
                <span className="hud-label text-[10px]">SELECT MODEL</span>
                <button onClick={handleClose} className="glass-circle w-7 h-7 flex items-center justify-center">
                  <X size={12} className="text-gray-400" />
                </button>
              </div>

              {/* Provider list */}
              <div className="px-3 pb-4 space-y-1">
                {providers.map((p) => {
                  const isAvail = available.has(p.id);
                  const isSelected = modelPreference === p.id;
                  const isExpanded = expandedProvider === p.id;
                  const hasModels = p.models.length > 1;

                  return (
                    <div key={p.id}>
                      <button
                        disabled={!isAvail}
                        onClick={() => {
                          if (isAvail) {
                            setModelPreference(p.id);
                            setModelOverride(null); // reset model override when switching provider
                            if (hasModels) {
                              setExpandedProvider(isExpanded ? null : p.id);
                            } else {
                              handleClose();
                            }
                          }
                        }}
                        className={clsx(
                          'w-full text-left px-4 py-3 hud-clip-sm transition-all flex items-center justify-between',
                          isSelected
                            ? 'bg-white/[0.06] border border-jarvis-blue/30'
                            : 'border border-transparent hover:bg-white/[0.03]',
                          !isAvail && 'opacity-30 cursor-not-allowed',
                        )}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[8px] font-mono text-gray-600 uppercase">{p.tag}</span>
                            <span className={clsx('text-xs font-semibold', isSelected ? 'text-white' : 'text-gray-300')}>
                              {p.label}
                            </span>
                            {!isAvail && <Lock size={9} className="text-gray-600" />}
                          </div>
                          <p className="text-[10px] text-gray-600 font-mono mt-0.5">{p.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isSelected && isAvail && (
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: p.color, boxShadow: `0 0 8px ${p.color}66` }}
                            />
                          )}
                          {hasModels && isSelected && (
                            isExpanded ? <ChevronUp size={12} className="text-gray-500" /> : <ChevronDown size={12} className="text-gray-500" />
                          )}
                        </div>
                      </button>

                      {/* Sub-models */}
                      {isExpanded && isSelected && hasModels && (
                        <div className="ml-4 mt-1 mb-2 space-y-0.5">
                          {p.models.map((m) => {
                            const isModelSelected = modelOverride === m.id || (!modelOverride && m.tier === 'balanced');
                            return (
                              <button
                                key={m.id}
                                onClick={() => {
                                  setModelOverride(m.id);
                                  handleClose();
                                }}
                                className={clsx(
                                  'w-full text-left px-3 py-2 text-[10px] font-mono transition-all hud-clip-sm',
                                  isModelSelected
                                    ? 'bg-white/[0.06] border border-white/10 text-white'
                                    : 'border border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/[0.02]',
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{m.label}</span>
                                  <span className={clsx('text-[8px] uppercase tracking-wider',
                                    m.tier === 'fast' ? 'text-green-500' :
                                    m.tier === 'premium' ? 'text-jarvis-gold' :
                                    'text-gray-600'
                                  )}>
                                    {m.tier}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
